import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

import { defaultAxiosInstance } from "services/client"
import {
  ApiStatus,
  GetCalendarData,
  GetCalendarRequestData,
} from "services/common"

// ---------- fetch calendar data for [Calender Tab] ---------- //
export interface GetCalendarResponse extends AxiosResponse {
  data: GetCalendarData
}

export interface GetCalendarRequest extends Partial<AxiosRequestConfig> {
  params: GetCalendarRequestData
}

const defaultCalendarUrl = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/properties/calendar`

export function getCalendarData(
  { params, method = "get", url = defaultCalendarUrl }: GetCalendarRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
) {
  return axiosInstance({
    params,
    method,
    url,
  }) as Promise<GetCalendarResponse>
}

// ---------- fetch fees and taxes data [pricing tab ]---------- //
export interface FetchFeesType {
  id: null | number
  description: string
  value: number | string | null
  isPercent: boolean
  isAirbnbFee: boolean
}

export interface FetchTaxesType {
  id: null | number
  type: string
  description: string
  typeOfTax: string
  value: number
  isPercent: boolean
  isAirbnbTax: boolean
  appliedOnFees: number[]
  appliedOnRent: boolean
  isAppliedOnRent?: boolean
  isDelete: boolean
  businessTaxId: null | string
  accommodationTaxRegistrationNumber: null | string
  exemptionsValue: string | null
  exemptionsMinDays: number | null
}

export interface FetchFeesAndTaxesType {
  minimumNonChargeableAdult: number | null
  fee_info: FetchFeesType[]
  tax_info: FetchTaxesType[]
}

export interface FetchFeesAndTaxesResponseData extends AxiosResponse {
  data: FetchFeesAndTaxesType
}

export interface FetchFeesAndTaxesResponse extends AxiosResponse {
  data: FetchFeesAndTaxesResponseData
}

export interface FetchFeesAndTaxesRequest extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
    unitId: string
  }
}

// ------ save fees and taxes data [pricing tab in review & publish screen] ------ //

export interface SaveFeesAndTaxesResponseData extends AxiosResponse {
  data: string
  message: string
}
export interface SaveFeesAndTaxesResponse extends AxiosResponse {
  data: SaveFeesAndTaxesResponseData
}
export interface SaveFeesAndTaxesRequest extends Partial<AxiosRequestConfig> {
  data: FetchFeesAndTaxesType
  params: {
    pmcId: string
    unitId: string
  }
}

// ---------- 'save edits' button on click Restricted check-in days section APIs [ Availability tab in review & publish screen ] ---------- //

export interface SaveCheckInDaysResponseData extends AxiosResponse {
  message: string
}

export interface SaveCheckInDaysResponse extends AxiosResponse {
  data: SaveCheckInDaysResponseData
}

export interface CheckInDaysType {
  pmcId: string
  unitId: string
  restrictedDays: string[]
}

export interface SaveCheckInDaysRequest extends Partial<AxiosRequestConfig> {
  data: CheckInDaysType
  params: {
    isRestrictedCheckIn: string
  }
}

// ---------- publish listing for RBO [step 6] ---------- //
export interface PublishPropertyForRBOResponse extends AxiosResponse {
  data: {
    message: string
  }
}

export interface PublishPropertyForRBORequestData {
  pmcId: string
  eventType: string
  pmsUnitId: string
}
export interface PublishPropertyForRBORequest
  extends Partial<AxiosRequestConfig> {
  params: PublishPropertyForRBORequestData
}

const defaultPublishPropertyURL = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/properties/rbo-publish-property`

export function publishPropertyForRBO(
  {
    params,
    method = "post",
    url = defaultPublishPropertyURL,
    ...config
  }: PublishPropertyForRBORequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<PublishPropertyForRBOResponse> {
  return axiosInstance({
    method,
    url: `${url}`,
    data: params,
    ...config,
  }) as Promise<PublishPropertyForRBOResponse>
}

// ---------- save calender data for review & publish [Calender tab in review & publish screen] [step 6] ---------- //

export interface SaveCalenderDataResponseData extends AxiosResponse {
  message: string
}

export interface SaveCalenderDataResponse extends AxiosResponse {
  data: SaveCalenderDataResponseData
}

export interface SaveCalenderDataType {
  dates: string[]
  basePrice: number | null
  available: boolean | null
  minStayDays: number | null
  maxStayDays: number | null
}
export interface SaveCalenderDataRequest extends Partial<AxiosRequestConfig> {
  data: SaveCalenderDataType
  params: {
    pmcId: string
    pmsUnitId: string
  }
}

const defaultCalenderURL = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/calendar/update-calendar-data`

export function saveCalenderForReviewAndPublishApi(
  {
    data,
    params,
    method = "post",
    url = defaultCalenderURL,
    ...config
  }: SaveCalenderDataRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<SaveCalenderDataResponse> {
  return axiosInstance({
    method,
    url: `${url}?pmcId=${params?.pmcId}&pmsUnitId=${params?.pmsUnitId}`,
    data,
    ...config,
  }) as Promise<SaveCalenderDataResponse>
}

// --------- update Custom Weekend Price data [Pricing tab in review & publish screen] [step 6] ---------- //
export interface UpdateCustomWeekendPriceResponse extends AxiosResponse {
  data: {
    apiStatus: ApiStatus
    message: string
    data: boolean
  }
}
export interface UpdateCustomWeekendPriceRequestData {
  pmcId: string
  pmsUnitId: string
  weekendPrice: number
}

export interface UpdateCustomWeekendPriceRequest
  extends Partial<AxiosRequestConfig> {
  data: UpdateCustomWeekendPriceRequestData
}

// ----------- get cancellation policy data [Policies & Rules tab in review & publish screen] [step 6] ---------- //
export interface PolicyType {
  category: string
  description: string
  id: string
  percentage: number
  validFrom: number | null
  validTo: number | null
}

export interface GetCancellationPolicyResponseDataData {
  gracePeriod: string | null
  gracePeriodPercentage: string | null
  policy: PolicyType[]
}

export interface GetCancellationPolicyResponseData extends AxiosResponse {
  apiStatus: ApiStatus
  message: string
  data: GetCancellationPolicyResponseDataData
}
export interface GetCancellationPolicyResponse extends AxiosResponse {
  data: GetCancellationPolicyResponseData
}

export interface GetCancellationRequestType {
  pmcId: string
  unitId: string
}

export interface GetCancellationPolicyRequest
  extends Partial<AxiosRequestConfig> {
  params: GetCancellationRequestType
}

const defaultCancellationPolicyURL = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/rbo/cancellation-policy`

export function getCancellationPolicyApi(
  {
    params,
    method = "get",
    url = defaultCancellationPolicyURL,
    ...config
  }: GetCancellationPolicyRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<GetCancellationPolicyResponse> {
  return axiosInstance({
    method,
    url: `${url}?pmcId=${params?.pmcId}&unitId=${params?.unitId}`,
    ...config,
  }) as Promise<GetCancellationPolicyResponse>
}

// ---------- save cancellation policy data [Policies & Rules tab in review & publish screen] [step 6] ---------- //

export interface SaveCancellationPolicyResponse extends AxiosResponse {
  data: SaveCalenderDataResponseData
}

export interface SaveCancellationPolicyRequest
  extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
    unitId: string
  }
  data: GetCancellationPolicyResponseDataData
}

export function saveCancellationPolicyApi(
  {
    params,
    data,
    method = "post",
    url = defaultCancellationPolicyURL,
    ...config
  }: SaveCancellationPolicyRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<SaveCancellationPolicyResponse> {
  return axiosInstance({
    method,
    url: `${url}?pmcId=${params?.pmcId}&unitId=${params?.unitId}`,
    data,
    ...config,
  }) as Promise<SaveCancellationPolicyResponse>
}

// ------------ get PDP data for property [Policies & Rules tab in review & publish screen] [step 6] ---------- //

export interface PdpDataType {
  isMyOwnPlan: boolean
  isWhimstayPlan: boolean
}
export interface GetPdpDataResponseData extends AxiosResponse {
  data: PdpDataType
}
export interface GetPdpDataResponse extends AxiosResponse {
  data: GetPdpDataResponseData
}

const defaultPDPURL = `${process.env.REACT_APP_BASE_API_URL || ""}/rbo/pdp`

export function getPdpDataApi(
  {
    params,
    method = "get",
    url = defaultPDPURL,
    ...config
  }: GetCancellationPolicyRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<GetPdpDataResponse> {
  return axiosInstance({
    method,
    url: `${url}?pmcId=${params?.pmcId}&unitId=${params?.unitId}`,
    ...config,
  }) as Promise<GetPdpDataResponse>
}

// ---------- save PDP data for property [Policies & Rules tab in review & publish screen] [step 6] ---------- //
export interface SavePdpDataResponse extends AxiosResponse {
  data: SaveCalenderDataResponseData
}
export interface SavePdpDataRequest extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
    unitId: string
  }
  data: PdpDataType
}

export function savePdpDataApi(
  {
    params,
    data,
    method = "post",
    url = defaultPDPURL,
    ...config
  }: SavePdpDataRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<SavePdpDataResponse> {
  return axiosInstance({
    method,
    url: `${url}?pmcId=${params?.pmcId}&unitId=${params?.unitId}`,
    data,
    ...config,
  }) as Promise<SavePdpDataResponse>
}

// ---------- update calendar pricing data [ review & publish screen ]  ---------- //
export interface UpdateCalendarPricingResponse extends AxiosResponse {
  data: SaveCalenderDataResponseData
}

export interface UpdateCalendarPricingRequestData {
  dates: string[]
  basePricePerNight: number | null
  lastMinuteDiscount: number | null
  extendedStayDiscount: number | null
  lastMinuteDiscountDates: string[]
  extendedMinuteDiscountDates: string[]
}

export interface UpdateCalendarPricingRequest
  extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
    pmsUnitId: string
  }
  data: UpdateCalendarPricingRequestData
}

const defaultUpdateCalendarURL = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/calendar/update-calendar-pricing`

export function updateCalendarPricingDataApi(
  {
    params,
    data,
    method = "post",
    url = defaultUpdateCalendarURL,
    ...config
  }: UpdateCalendarPricingRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<UpdateCalendarPricingResponse> {
  return axiosInstance({
    method,
    params,
    url,
    data,
    ...config,
  }) as Promise<UpdateCalendarPricingResponse>
}

// ---------- update calendar availability data [ review & publish screen ]  ---------- //
export interface UpdateCalendarAvailabilityResponse extends AxiosResponse {
  data: SaveCalenderDataResponseData
}

export interface UpdateCalendarAvailabilityRequestData {
  dates: string[]
  isBlocked: boolean | null
  minStayDays: number | null
  maxStayDays: number | null
}

export interface UpdateCalendarAvailabilityRequest
  extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
    pmsUnitId: string
  }
  data: UpdateCalendarAvailabilityRequestData
}

const defaultUpdateCalendarAvailabilityURL = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/calendar/update-calendar-data`

export function updateCalendarAvailabilityDataApi(
  {
    params,
    data,
    method = "post",
    url = defaultUpdateCalendarAvailabilityURL,
    ...config
  }: UpdateCalendarAvailabilityRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<UpdateCalendarAvailabilityResponse> {
  return axiosInstance({
    method,
    params,
    url,
    data,
    ...config,
  }) as Promise<UpdateCalendarAvailabilityResponse>
}
